import { useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

interface TrackingPayload {
  client_session_id: string
  event_type: string
  page_path?: string
  page_title?: string
  referrer?: string
  user_agent?: string
  duration_seconds?: number
  payload?: any
  form_name?: string
  partial_data?: any
  drop_off_field?: string
  time_spent_seconds?: number
}

class AnalyticsTracker {
  private sessionId: string
  private sessionStartTime: number
  private pageStartTime: number
  private currentPath: string = ''
  private isActive: boolean = true
  private pendingEvents: TrackingPayload[] = []

  constructor() {
    this.sessionId = this.getOrCreateSessionId()
    this.sessionStartTime = Date.now()
    this.pageStartTime = Date.now()
    this.setupEventListeners()
    this.startSession()
  }

  private getOrCreateSessionId(): string {
    let sessionId = localStorage.getItem('analytics_session_id')
    if (!sessionId) {
      sessionId = crypto.randomUUID()
      localStorage.setItem('analytics_session_id', sessionId)
    }
    return sessionId
  }

  private setupEventListeners() {
    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.flushPendingEvents()
      }
    })

    // Handle page unload
    window.addEventListener('beforeunload', () => {
      this.endSession()
      this.flushPendingEvents()
    })

    // Handle page hide (mobile safari)
    window.addEventListener('pagehide', () => {
      this.endSession()
      this.flushPendingEvents()
    })
  }

  private async sendEvent(payload: TrackingPayload) {
    try {
      await fetch('https://itcjwpvspbzbojqyiiqn.supabase.co/functions/v1/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      })
    } catch (error) {
      console.warn('Analytics tracking failed:', error)
      // Store failed events to retry later
      this.pendingEvents.push(payload)
    }
  }

  private flushPendingEvents() {
    if (this.pendingEvents.length > 0) {
      // Try to send pending events with sendBeacon if available
      if (navigator.sendBeacon) {
        this.pendingEvents.forEach(event => {
          navigator.sendBeacon(
            'https://itcjwpvspbzbojqyiiqn.supabase.co/functions/v1/track',
            JSON.stringify(event)
          )
        })
      }
      this.pendingEvents = []
    }
  }

  private startSession() {
    this.sendEvent({
      client_session_id: this.sessionId,
      event_type: 'session_start',
      page_path: window.location.pathname,
      referrer: document.referrer,
      user_agent: navigator.userAgent
    })
  }

  public trackPageView(path: string, title?: string) {
    // End previous page if there was one
    if (this.currentPath) {
      this.trackPageLeave(this.currentPath)
    }

    this.currentPath = path
    this.pageStartTime = Date.now()

    this.sendEvent({
      client_session_id: this.sessionId,
      event_type: 'page_view',
      page_path: path,
      page_title: title || document.title,
      referrer: document.referrer
    })
  }

  public trackPageLeave(path: string) {
    const duration = Math.floor((Date.now() - this.pageStartTime) / 1000)
    
    this.sendEvent({
      client_session_id: this.sessionId,
      event_type: 'page_leave',
      page_path: path,
      duration_seconds: duration
    })
  }

  private endSession() {
    if (!this.isActive) return
    
    const duration = Math.floor((Date.now() - this.sessionStartTime) / 1000)
    this.isActive = false

    this.sendEvent({
      client_session_id: this.sessionId,
      event_type: 'session_end',
      duration_seconds: duration
    })
  }

  public trackFormStart(formName: string, pagePath: string) {
    this.sendEvent({
      client_session_id: this.sessionId,
      event_type: 'form_start',
      form_name: formName,
      page_path: pagePath
    })
  }

  public trackFormInput(formName: string, partialData: any, dropOffField: string, timeSpentSeconds: number) {
    this.sendEvent({
      client_session_id: this.sessionId,
      event_type: 'form_input',
      form_name: formName,
      partial_data: partialData,
      drop_off_field: dropOffField,
      time_spent_seconds: timeSpentSeconds
    })
  }

  public trackFormSubmit(formName: string, timeSpentSeconds: number) {
    this.sendEvent({
      client_session_id: this.sessionId,
      event_type: 'form_submit',
      form_name: formName,
      time_spent_seconds: timeSpentSeconds
    })
  }

  public trackFormAbandon(formName: string, dropOffField: string, timeSpentSeconds: number) {
    this.sendEvent({
      client_session_id: this.sessionId,
      event_type: 'form_abandon',
      form_name: formName,
      drop_off_field: dropOffField,
      time_spent_seconds: timeSpentSeconds
    })
  }

  public trackClick(element: string, pagePath: string) {
    this.sendEvent({
      client_session_id: this.sessionId,
      event_type: 'click',
      page_path: pagePath,
      payload: { element }
    })
  }

  public trackScroll(maxDepth: number, pagePath: string) {
    this.sendEvent({
      client_session_id: this.sessionId,
      event_type: 'scroll',
      page_path: pagePath,
      payload: { max_depth: maxDepth }
    })
  }

  public getSessionId(): string {
    return this.sessionId
  }
}

// Singleton instance
let trackerInstance: AnalyticsTracker | null = null

export const useTracking = () => {
  const location = useLocation()
  const trackerRef = useRef<AnalyticsTracker | null>(null)

  useEffect(() => {
    if (!trackerInstance) {
      trackerInstance = new AnalyticsTracker()
    }
    trackerRef.current = trackerInstance
  }, [])

  useEffect(() => {
    if (trackerRef.current) {
      trackerRef.current.trackPageView(location.pathname, document.title)
    }
  }, [location.pathname])

  const trackFormStart = useCallback((formName: string) => {
    trackerRef.current?.trackFormStart(formName, location.pathname)
  }, [location.pathname])

  const trackFormInput = useCallback((formName: string, partialData: any, dropOffField: string, timeSpentSeconds: number) => {
    trackerRef.current?.trackFormInput(formName, partialData, dropOffField, timeSpentSeconds)
  }, [])

  const trackFormSubmit = useCallback((formName: string, timeSpentSeconds: number) => {
    trackerRef.current?.trackFormSubmit(formName, timeSpentSeconds)
  }, [])

  const trackFormAbandon = useCallback((formName: string, dropOffField: string, timeSpentSeconds: number) => {
    trackerRef.current?.trackFormAbandon(formName, dropOffField, timeSpentSeconds)
  }, [])

  const trackClick = useCallback((element: string) => {
    trackerRef.current?.trackClick(element, location.pathname)
  }, [location.pathname])

  const trackScroll = useCallback((maxDepth: number) => {
    trackerRef.current?.trackScroll(maxDepth, location.pathname)
  }, [location.pathname])

  return {
    trackFormStart,
    trackFormInput,
    trackFormSubmit,
    trackFormAbandon,
    trackClick,
    trackScroll,
    sessionId: trackerRef.current?.getSessionId()
  }
}